'use client'

import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { E_EARN_VAULT_CHAIN_ID, E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { paths } from '@/config/paths'
import { useEVaultPositions } from '@/domain/euler/positions/useEVaultPositions'
import { useUserCollateralState } from '@/domain/euler/rebalance'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfoQuery } from '@/domain/market-info/useMarketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { useEulerPositions } from '@/features/euler-portfolio/logic/useEulerPositions'
import { useMyPortfolio } from '@/features/isolated-portfolio/logic/useMyPortfolio'
import { PageHeroStats } from '@/ui/molecules/page-hero/PageHeroStats'
import { usePathname } from 'next/navigation'
import { useMemo } from 'react'
import type { Address } from 'viem'
import { useAccount } from 'wagmi'

function formatUsd(value: number): string {
  return USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(value), { compact: true })
}

function DashboardPositionStats() {
  const pathname = usePathname()

  if (pathname === paths.dashboardPooled) {
    return <PooledDashboardStats />
  }

  if (pathname === paths.dashboardLendBorrow) {
    return <LendBorrowDashboardStats />
  }

  if (pathname === paths.dashboardEarn) {
    return <EarnDashboardStats />
  }

  if (pathname === paths.dashboardIsolated) {
    return <IsolatedDashboardStats />
  }

  return null
}

function PooledDashboardStats() {
  const { chainId } = usePageChainId()
  const { marketInfo, isPending } = useMarketInfoQuery({ chainId, refetchInterval: QUERY_REFETCH_INTERVAL })
  const supply = marketInfo?.userPositionSummary.totalCollateralUSD.toNumber() ?? 0
  const borrow = marketInfo?.userPositionSummary.totalBorrowsUSD.toNumber() ?? 0
  const netEquity = supply - borrow

  return (
    <PageHeroStats
      stats={[
        { label: 'Supplied', value: formatUsd(supply) },
        { label: 'Borrowed', value: formatUsd(borrow) },
        { label: 'Net Equity', value: formatUsd(netEquity) },
      ]}
      loading={isPending}
    />
  )
}

function LendBorrowDashboardStats() {
  const { address } = useAccount()
  const { elendPositions, eborrowPositions, isLoading: isLoadingPositions } = useEulerPositions()

  const vaultAddresses = useMemo(() => {
    const vaults = new Set<Address>()
    for (const position of elendPositions) vaults.add(position.vaultAddress)
    for (const position of eborrowPositions) {
      vaults.add(position.collateralVaultAddress)
      vaults.add(position.liabilityVaultAddress)
    }
    return Array.from(vaults)
  }, [elendPositions, eborrowPositions])

  const { state, isLoading: isLoadingState } = useUserCollateralState({
    vaultAddresses,
    userAddress: address,
    chainId: E_LEND_CHAIN_ID,
    enabled: !!address && vaultAddresses.length > 0,
  })

  const totals = useMemo(() => {
    if (!state) return { supply: 0, borrow: 0, netEquity: 0 }
    const supply = state.collaterals.reduce((sum, collateral) => sum + collateral.valueUsd, 0)
    const borrow = state.borrows.reduce((sum, borrow) => sum + borrow.debtUsd, 0)
    return { supply, borrow, netEquity: supply - borrow }
  }, [state])

  const isLoading = isLoadingPositions || (vaultAddresses.length > 0 && isLoadingState)

  return (
    <PageHeroStats
      stats={[
        { label: 'Supplied', value: formatUsd(totals.supply) },
        { label: 'Borrowed', value: formatUsd(totals.borrow) },
        { label: 'Net Equity', value: formatUsd(totals.netEquity) },
      ]}
      loading={isLoading}
    />
  )
}

function EarnDashboardStats() {
  const { positions, isLoading } = useEVaultPositions({ chainId: E_EARN_VAULT_CHAIN_ID })
  const totalDeposited = positions.reduce((sum, position) => sum + position.depositAmountUSD.toNumber(), 0)

  return (
    <PageHeroStats
      stats={[
        { label: 'Deposited', value: formatUsd(totalDeposited) },
        { label: 'Positions', value: String(positions.length) },
      ]}
      loading={isLoading}
    />
  )
}

function IsolatedDashboardStats() {
  const { deposits, borrows, isLoading } = useMyPortfolio()
  const supply = deposits.reduce(
    (sum, deposit) => sum + deposit.collateralToken.toUSD(deposit.lendAmount).toNumber(),
    0,
  )
  const borrow = borrows.reduce((sum, borrow) => sum + borrow.borrowToken.toUSD(borrow.borrowAmount).toNumber(), 0)
  const netEquity = supply - borrow

  return (
    <PageHeroStats
      stats={[
        { label: 'Supplied', value: formatUsd(supply) },
        { label: 'Borrowed', value: formatUsd(borrow) },
        { label: 'Net Equity', value: formatUsd(netEquity) },
      ]}
      loading={isLoading}
    />
  )
}

export function DashboardStatsLoader() {
  const { address } = useAccount()

  if (!address) return null

  return <DashboardPositionStats />
}
